import React, { ForwardedRef, useCallback, useMemo } from 'react';

import { Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { StreamTypes } from 'store/player/actions.types';
import {
    PlaybackTypes,
    SearchDeeplink,
    SearchProgram,
} from 'store/railsBuilder/reducer.types';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';

import { ContentType } from 'services/data-manager/types';

import isFunction from 'utils/function';
import getOrdinal from 'utils/getOrdinary';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';

import { DetailsRequestUrlType } from 'screens/Detail/types';

import BaseTileImage from 'components/BaseTileImage';
import RibbonTouchable from 'components/CustomButtons/RibbonTouchable';
import {
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { LockedContentCard } from 'components/Ribbon/components/LockedContentCard';
import SearchProgramMarkers from 'components/Ribbon/components/SearchProgramMarkers';
import { TileTitle } from 'components/Ribbon/components/TileTitle';
import useSearchParentalRating from 'components/Ribbon/hooks/useSearchParentalRating';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import { PLACEHOLDER_ICON_CENTER } from '../../constants';
import getSearchDeeplinkOpeningParams from '../SearchDeeplink/utils/getSearchDeeplinkOpeningParams';

type Props = {
    item: SearchProgram;
    isCollection?: boolean;
    testID?: string;
    index?: number;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    totalNumberOfItems?: number;
} & OnUavAnalyticsProps &
    StandardUavProps;

const {
    CARDS_TITLE_CONTENT_LOCKED,
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

function SearchVodRibbon(
    {
        item,
        isCollection = false,
        onRailsSelection,
        railId,
        templateId,
        testID,
        index = 0,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        totalNumberOfItems,
    }: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigation =
        useNavigation<
            StackNavigationProp<RouteNavigationParams, SCREEN.SEARCH>
        >();

    const profileParentalControlRating = useProfileParentalRating();
    const parentalRating = useSearchParentalRating(item);
    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const { id, title, seasonNumber, episodeNumber, episode, series } = item;

    const contentType = item.contentType ?? ImageContentType.MOVIE;
    const handleOpenDetailsPage = useCallback(() => {
        dispatch(
            startSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering),
        );

        navigation.push(SCREEN.DETAILS, {
            id,
            editorialId: id,
            playbackType: PlaybackTypes.vod_ed,
            filterType: DetailsRequestUrlType.VOD,
            season: seasonNumber,
            episode: episodeNumber,
            seriesId: series,
            railId,
            templateId,
        });
    }, [
        navigation,
        id,
        seasonNumber,
        episodeNumber,
        series,
        railId,
        templateId,
    ]);

    const handlePress = useCallback(() => {
        const { link } = getSearchDeeplinkOpeningParams(
            item as unknown as SearchDeeplink,
        );

        onRailsSelection?.({
            id,
            playbackType: StreamTypes.vod,
            deeplinkProviderId: link?.provider || '',
            providerResourceId: '',
            index,
        });

        if (isContentLocked && isFunction(onShowPinDialog)) {
            if (isFunction(onSetParentalControlCheckedCallback)) {
                onSetParentalControlCheckedCallback(handleOpenDetailsPage);
            }

            onShowPinDialog();
        } else {
            handleOpenDetailsPage();
        }
    }, [
        id,
        isContentLocked,
        handleOpenDetailsPage,
        onRailsSelection,
        onShowPinDialog,
        onSetParentalControlCheckedCallback,
        item,
        index,
    ]);

    const accessibilityLabel = useMemo(() => {
        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (isContentLocked && index === 0) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        if (isContentLocked) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        let label = '';
        if (seasonNumber) {
            label += `${t('HOVER_SEASON')}${seasonNumber} `;
        }

        if (episodeNumber) {
            label += `${t('HOVER_EPISODE')}${episodeNumber} `;
        }

        label += episode || title;
        label += ' ';

        if (index === 0) {
            label += `${t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        } else {
            label += `${t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        return label;
    }, [
        isContentLocked,
        seasonNumber,
        episodeNumber,
        episode,
        title,
        t,
        totalNumberOfItems,
        index,
        isCollection,
    ]);

    return (
        <>
            <RibbonTouchable
                ref={forwardedRef}
                testID={testID}
                metadataType={ContentType.SearchVod}
                isCollection={isCollection}
                onPress={handlePress}
                railId={railId}
                accessibilityLabel={accessibilityLabel}
                RibbonContentComponent={
                    isContentLocked ? (
                        <LockedContentCard />
                    ) : (
                        <>
                            <BaseTileImage
                                style={gStyles.flex}
                                placeHolder={PLACEHOLDER_ICON_CENTER}
                                resizeMode="cover"
                                contentId={id}
                                contentType={
                                    series
                                        ? ImageContentType.EPISODE
                                        : contentType
                                }
                                imageType={
                                    series
                                        ? ImageType.EPISODE
                                        : ImageType.BANNER
                                }
                                width={ImageSize.LANDSCAPE.width}
                            />
                            <SearchProgramMarkers
                                searchItem={item}
                                isLive={false}
                            />
                        </>
                    )
                }
                RibbonInfoComponent={
                    <TileTitle
                        title={
                            isContentLocked ? (
                                t(CARDS_TITLE_CONTENT_LOCKED)
                            ) : (
                                <>
                                    {!!seasonNumber && (
                                        <Text>{`${t(
                                            'HOVER_SEASON',
                                        )}${seasonNumber} `}</Text>
                                    )}
                                    {!!episodeNumber && (
                                        <Text>
                                            {`${t(
                                                'HOVER_EPISODE',
                                            )}${episodeNumber} `}
                                        </Text>
                                    )}
                                    {episode || title}
                                </>
                            )
                        }
                    />
                }
            />
        </>
    );
}

export default React.forwardRef(SearchVodRibbon);
